import { useCallback, useEffect, useRef, useState } from 'react';

import {
	filterOptions,
	getDisplayText,
	getNextFocusedIndex,
	getOptionToSelect,
	getResetState,
	shouldCloseDropdown,
	shouldOpenDropdown
} from '../helpers/navigationMenuHelpers';
import { OptionType } from '../NavigationMenu';

export interface UseNavigationMenuParams {
	options: OptionType[];
	selectedValue: OptionType;
	onSelectChange: (selected: OptionType) => void;
	placeholder: string;
	disabled?: boolean;
}

interface UseNavigationMenuReturn {
	// State
	isOpen: boolean;
	searchTerm: string;
	focusedIndex: number;
	filteredOptions: OptionType[];
	displayText: string;

	// Refs
	containerRef: React.RefObject<HTMLDivElement>;
	triggerRef: React.RefObject<HTMLButtonElement>;
	listboxRef: React.RefObject<HTMLUListElement>;
	searchInputRef: React.RefObject<HTMLInputElement>;

	// Handlers
	handleToggle: () => void;
	handleOptionSelect: (option: OptionType) => void;
	handleKeyDown: (event: React.KeyboardEvent) => void;
	handleSearchChange: (value: string) => void;
	setFocusedIndex: (index: number) => void;
}

export const useNavigationMenu = ({
	options,
	selectedValue,
	onSelectChange,
	placeholder,
	disabled = false
}: UseNavigationMenuParams): UseNavigationMenuReturn => {
	// State
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [focusedIndex, setFocusedIndex] = useState(-1);

	// Refs
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const listboxRef = useRef<HTMLUListElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Derived state
	const filteredOptions = filterOptions(options, searchTerm);
	const displayText = getDisplayText(selectedValue, placeholder);

	// Handlers
	const handleToggle = useCallback(() => {
		if (disabled) {
			return;
		}
		setIsOpen(!isOpen);
	}, [disabled, isOpen]);

	const handleOptionSelect = useCallback(
		(option: OptionType) => {
			onSelectChange(option);
			const resetState = getResetState();
			setIsOpen(resetState.isOpen);
			setSearchTerm(resetState.searchTerm);
			setFocusedIndex(resetState.focusedIndex);
		},
		[onSelectChange]
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (disabled) {
				return;
			}

			const { key } = event;
			const { activeElement } = document;

			// Handle opening dropdown
			if (shouldOpenDropdown(key, isOpen)) {
				event.preventDefault();
				setIsOpen(true);
				setFocusedIndex(0);
				return;
			}

			// Handle closing dropdown
			if (shouldCloseDropdown(key, isOpen)) {
				// Don't prevent default for Tab unless we want to trap focus
				if (key === 'Escape') {
					event.preventDefault();
				}
				const resetState = getResetState();
				setIsOpen(resetState.isOpen);
				setSearchTerm(resetState.searchTerm);
				setFocusedIndex(resetState.focusedIndex);
				if (key === 'Escape') {
					triggerRef.current?.focus();
				}
				return;
			}

			// Handle Tab - let natural tab flow work, only intervene when leaving dropdown
			if (isOpen && key === 'Tab') {
				// Don't prevent default - let the browser handle natural tab flow
				// We'll only close the dropdown when Tab would naturally leave our dropdown area

				// Use setTimeout to check after the browser processes the tab
				setTimeout(() => {
					const newActiveElement = document.activeElement;
					const dropdownContainer = containerRef.current?.querySelector(
						'.navigation-menu__dropdown'
					);

					// If focus has moved outside our dropdown container, close the dropdown
					if (
						dropdownContainer &&
						newActiveElement &&
						!dropdownContainer.contains(newActiveElement)
					) {
						const resetState = getResetState();
						setIsOpen(resetState.isOpen);
						setSearchTerm(resetState.searchTerm);
						setFocusedIndex(resetState.focusedIndex);
					}
				}, 0);

				// Let the Tab event continue naturally
				return;
			}

			// Handle option selection
			const optionToSelect = getOptionToSelect(key, focusedIndex, filteredOptions, isOpen);
			if (optionToSelect) {
				event.preventDefault();
				handleOptionSelect(optionToSelect);
				triggerRef.current?.focus();
				return;
			}

			// Handle arrow navigation within open dropdown
			if (isOpen && (key === 'ArrowDown' || key === 'ArrowUp')) {
				event.preventDefault();

				// Arrow keys always navigate through the menu options
				// The search input maintains focus while we use virtual focus for options
				const nextIndex = getNextFocusedIndex(
					key === 'ArrowDown' ? 'down' : 'up',
					focusedIndex,
					filteredOptions.length
				);
				setFocusedIndex(nextIndex);

				// Keep search input focused for continued typing while virtually focusing options
				if (activeElement !== searchInputRef.current) {
					searchInputRef.current?.focus();
				}
			}
		},
		[disabled, isOpen, focusedIndex, filteredOptions, handleOptionSelect]
	);

	const handleSearchChange = useCallback((value: string) => {
		setSearchTerm(value);
		setFocusedIndex(0); // Focus first item when searching
	}, []);

	// Effects
	// Focus search input when dropdown opens and set virtual focus on first option
	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			// Small delay to ensure the dropdown is rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
				// Set virtual focus on first option for keyboard users
				if (filteredOptions.length > 0) {
					setFocusedIndex(0);
				}
			}, 10);
		}
	}, [isOpen, filteredOptions.length]);

	// Handle outside clicks
	useEffect(() => {
		const handleOutsideClick = (event: MouseEvent): void => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				const resetState = getResetState();
				setIsOpen(resetState.isOpen);
				setSearchTerm(resetState.searchTerm);
				setFocusedIndex(resetState.focusedIndex);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleOutsideClick);
			return () => document.removeEventListener('mousedown', handleOutsideClick);
		}
		return undefined;
	}, [isOpen]);

	// Update focused index when options change
	useEffect(() => {
		if (focusedIndex >= filteredOptions.length && filteredOptions.length > 0) {
			setFocusedIndex(filteredOptions.length - 1);
		}
	}, [filteredOptions.length, focusedIndex]);

	return {
		// State
		isOpen,
		searchTerm,
		focusedIndex,
		filteredOptions,
		displayText,

		// Refs
		containerRef,
		triggerRef,
		listboxRef,
		searchInputRef,

		// Handlers
		handleToggle,
		handleOptionSelect,
		handleKeyDown,
		handleSearchChange,
		setFocusedIndex
	};
};
